// src/components/PersonalReminders.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import "./PersonalReminders.css";
import config from "../config";
import { downloadICS } from "./calendar";
import {
  parseScheduleDateLocal,
  formatLocalDate,
  withLocalTime,
} from "./dateformatter";
import {
  canUseNotifications,
  requestNotifyPermission,
  tryNotify,
} from "./notify";

// ---- Time options ----------------------------------------------------------
const TIME_OPTIONS = {
  DAY_OF_6AM: "DAY_OF_6AM",
  DAY_BEFORE_6PM: "DAY_BEFORE_6PM",
};

function subtractDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() - n);
  return d;
}

function describeTimeOption(option) {
  switch (option) {
    case TIME_OPTIONS.DAY_OF_6AM:
      return "6:00 AM on the day of recycling";
    case TIME_OPTIONS.DAY_BEFORE_6PM:
      return "6:00 PM on the previous day";
    default:
      return "6:00 AM on the day of recycling";
  }
}

function computeEventStart(scheduleDateStr, option) {
  const { date, isDateOnly } = parseScheduleDateLocal(scheduleDateStr);
  if (option === TIME_OPTIONS.DAY_BEFORE_6PM) {
    const prev = subtractDays(date, 1);
    return withLocalTime(prev, 18, 0, 0); // 6PM previous day
  }
  if (isDateOnly) return withLocalTime(date, 6, 0, 0); // 6AM day-of
  return withLocalTime(date, 6, 0, 0);
}

// ---- Modal (no external deps) ----------------------------------------------
function Modal({ open, title, children, onClose, onSave, saveLabel = "Save" }) {
  if (!open) return null;
  return (
    <div className="pr-backdrop" onClick={onClose}>
      <div className="pr-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pr-modal-header">
          <h3 className="pr-modal-title">{title}</h3>
        </div>
        <div className="pr-modal-body">{children}</div>
        <div className="pr-modal-footer">
          <button type="button" onClick={onClose} className="pr-btn pr-btn-ghost">
            Cancel
          </button>
          <button type="button" onClick={onSave} className="pr-btn pr-btn-primary">
            {saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Card helpers ----------------------------------------------------------
function Card({ children }) {
  return <div className="pr-card">{children}</div>;
}
function CardRow({ label, children }) {
  return (
    <div className="pr-row">
      <div className="pr-row-label">{label}</div>
      <div className="pr-row-value">{children}</div>
    </div>
  );
}

// ---- Main component --------------------------------------------------------
export default function PersonalReminders({ townshipId }) {
  const effectiveTownshipId = townshipId || "montgomery-nj";
  const [schedules, setSchedules] = useState([]);
  const [isLoadingSchedules, setIsLoadingSchedules] = useState(true);

  const remindersStorageKey = `reminders_${effectiveTownshipId}`;
  const [reminders, setReminders] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(remindersStorageKey) || "[]");
    } catch {
      return [];
    }
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [modalNote, setModalNote] = useState("");
  const [modalSchedule, setModalSchedule] = useState(null);
  const [editingReminderId, setEditingReminderId] = useState(null);
  const [timeOption, setTimeOption] = useState(TIME_OPTIONS.DAY_OF_6AM); // used in modal + header bulk add

  // Fetch schedules
useEffect(() => {
  const fetchSchedules = async () => {
    setIsLoadingSchedules(true);
    try {
      const url = `${config.API_BASE_URL}/api/consumer/schedule?townshipId=${encodeURIComponent(
        effectiveTownshipId
      )}`;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();

      // Extra safety: if backend returns all towns, filter on client too
      const filtered = Array.isArray(data)
        ? data.filter((s) => s.townshipId === effectiveTownshipId || !s.townshipId)
        : [];

      filtered.sort((a, b) => new Date(a.date) - new Date(b.date));
      setSchedules(filtered);
    } catch (err) {
      console.error("Error fetching schedules:", err);
      setSchedules([]);
    } finally {
      setIsLoadingSchedules(false);
    }
  };

  fetchSchedules();
}, [effectiveTownshipId]);


  // Persist reminders
  useEffect(() => {
    localStorage.setItem(remindersStorageKey, JSON.stringify(reminders));
  }, [reminders, remindersStorageKey]);

useEffect(() => {
  try {
    setReminders(JSON.parse(localStorage.getItem(remindersStorageKey) || "[]"));
  } catch {
    setReminders([]);
  }
}, [remindersStorageKey]);


  // Request notification permission
  useEffect(() => {
    if (canUseNotifications()) requestNotifyPermission().catch(() => {});
  }, []);

  // Simple heuristic notifications
  const checkDueReminders = useCallback(() => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    reminders.forEach((r) => {
      const d = new Date(r.date);
      const dueToday = d.toDateString() === today.toDateString();
      const dueTomorrow = d.toDateString() === tomorrow.toDateString();

      if (r.timeOption === TIME_OPTIONS.DAY_BEFORE_6PM) {
        const prev = subtractDays(d, 1);
        if (prev.toDateString() === today.toDateString()) {
          tryNotify("♻️ NeuroGreen Reminder", {
            body: `${r.note} - scheduled for ${d.toLocaleDateString()} (6 PM today)`,
          });
          return;
        }
      }
      if (dueToday || dueTomorrow) {
        tryNotify("♻️ NeuroGreen Reminder", {
          body: `${r.note} - scheduled for ${d.toLocaleDateString()}`,
        });
      }
    });
  }, [reminders]);

  useEffect(() => checkDueReminders(), [checkDueReminders]);

  // Maps & filters
  const reminderByDate = useMemo(() => {
    const map = new Map();
    reminders.forEach((r) => map.set(r.date, r));
    return map;
  }, [reminders]);

  // future (and today) only
  const upcomingSchedules = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return schedules.filter((s) => new Date(s.date) >= today);
  }, [schedules]);

  // Modal open/close
  const openSetReminder = (schedule) => {
    setEditingReminderId(null);
    setModalSchedule(schedule);
    setModalNote(`Reminder to put out ${schedule.type} bin`);
    setTimeOption(TIME_OPTIONS.DAY_OF_6AM);
    setModalOpen(true);
  };
  const openEditReminder = (schedule, reminder) => {
    setEditingReminderId(reminder.id);
    setModalSchedule(schedule);
    setModalNote(reminder.note);
    setTimeOption(reminder.timeOption || TIME_OPTIONS.DAY_OF_6AM);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setModalSchedule(null);
    setModalNote("");
    setEditingReminderId(null);
  };

  // Save reminder
  const saveReminderFromModal = () => {
    if (!modalSchedule) return;
    const { date, type } = modalSchedule;
    setReminders((prev) => {
      const existing = prev.find((r) => r.date === date);
      if (existing) {
        return prev.map((r) =>
          r.date === date ? { ...r, note: modalNote, timeOption } : r
        );
      }
      return [
        ...prev,
        { id: Date.now().toString(), date, type, note: modalNote, timeOption },
      ];
    });
    closeModal();
  };

  const deleteReminder = (id) =>
    setReminders((prev) => prev.filter((r) => r.id !== id));

  // Calendar helpers
  const addToCalendar = (schedule, note, selectedTimeOption) => {
    const startLocal = computeEventStart(
      schedule.date,
      selectedTimeOption || TIME_OPTIONS.DAY_OF_6AM
    );
    downloadICS({
      title: `♻️ ${schedule.type} Pickup`,
      description: note || schedule.description || "NeuroGreen",
      start: startLocal,
      durationMinutes: 15,
      alarms: ["-P1D", "-PT1H"],
    });
  };

  const addAllToCalendar = (bulkTimeOption = TIME_OPTIONS.DAY_OF_6AM) => {
    upcomingSchedules.forEach((schedule) => {
      const reminder = reminderByDate.get(schedule.date);
      addToCalendar(schedule, reminder?.note, bulkTimeOption);
    });
    alert(
      `Added ${upcomingSchedules.length} upcoming events (${describeTimeOption(
        bulkTimeOption
      )}) to your calendar.`
    );
  };

  // ---- Render --------------------------------------------------------------
  return (
    <div className="pr-container">
      <div className="Schedule-header">
      <h3 className="Schedule-title">
        Upcoming Recycling Schedule
        <span className="pr-muted"> ({effectiveTownshipId})</span>
      </h3>

        {upcomingSchedules.length > 0 && (
          <div className="Schedule-controls">
            <select
              className="Schedule-timeSelect"
              value={timeOption}
              onChange={(e) => setTimeOption(e.target.value)}
            >
              <option value="06:00">6:00 AM (day of recycling)</option>
              <option value="18:00">6:00 PM (day before)</option>
            </select>
            <button className="Schedule-addAll"
              type="button"
              onClick={() => addAllToCalendar(timeOption)}
            >
              📅 Add All to Calendar
            </button>
          </div>
        )}
      </div>

      <div className="pr-cards-grid">
        {isLoadingSchedules ? (
          <div className="pr-loading">
            <div className="pr-spinner"></div>
            <p>Loading schedules from server...</p>
            <p className="pr-muted" style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
              Sometimes the request may take up to 30 seconds
            </p>
          </div>
        ) : upcomingSchedules.length === 0 ? (
          <div className="pr-empty">No upcoming schedules.</div>
        ) : null}

        {!isLoadingSchedules && upcomingSchedules.map((schedule, i) => {
          const reminder = reminderByDate.get(schedule.date);
          const { date } = parseScheduleDateLocal(schedule.date);
          return (
            <Card key={i}>
              <CardRow label="Date">
                <strong>{formatLocalDate(date)}</strong>
              </CardRow>
              <CardRow label="Type">{schedule.type}</CardRow>
              <CardRow label="Description">
                <span className="pr-muted">{schedule.description}</span>
              </CardRow>

              <div className="pr-hr" />

              {reminder ? (
                <div>
                  <div className="pr-reminder-title">Reminder</div>
                  <div className="pr-note-box">
                    <div>{reminder.note}</div>
                    <div className="pr-note-time">
                      Time: {describeTimeOption(reminder.timeOption)}
                    </div>
                  </div>
                  <div className="pr-btn-row">
                    <button
                      onClick={() => openEditReminder(schedule, reminder)}
                      className="pr-btn"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteReminder(reminder.id)}
                      className="pr-btn pr-btn-danger"
                    >
                      Delete
                    </button>
                    <button
                      onClick={() =>
                        addToCalendar(
                          schedule,
                          reminder?.note,
                          reminder?.timeOption
                        )
                      }
                      className="pr-btn pr-btn-secondary"
                    >
                      Add to Calendar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="pr-btn-row">
                  <button
                    onClick={() => openSetReminder(schedule)}
                    className="pr-btn pr-btn-primary"
                  >
                    Set Reminder
                  </button>
                  <button
                    onClick={() =>
                      addToCalendar(schedule, undefined, TIME_OPTIONS.DAY_OF_6AM)
                    }
                    className="pr-btn pr-btn-secondary"
                  >
                    Add to Calendar
                  </button>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Modal */}
      <Modal
        open={modalOpen}
        title={editingReminderId ? "Edit Reminder" : "Set Reminder"}
        onClose={closeModal}
        onSave={saveReminderFromModal}
        saveLabel={editingReminderId ? "Save" : "Add Reminder"}
      >
        {modalSchedule && (
          <div className="pr-modal-subtle">
            For <b>{modalSchedule.type}</b> pickup on{" "}
            <b>{formatLocalDate(parseScheduleDateLocal(modalSchedule.date).date)}</b>
          </div>
        )}

        <label className="pr-label">Note</label>
        <textarea
          value={modalNote}
          onChange={(e) => setModalNote(e.target.value)}
          rows={3}
          placeholder="Reminder to put out plastic bin"
          className="pr-textarea"
        />

        <div className="pr-spacer-12" />
        <label className="pr-label">Reminder time</label>
        <div className="pr-radio-group">
          <label className="pr-radio-item">
            <input
              type="radio"
              name="reminderTime"
              value={TIME_OPTIONS.DAY_OF_6AM}
              checked={timeOption === TIME_OPTIONS.DAY_OF_6AM}
              onChange={() => setTimeOption(TIME_OPTIONS.DAY_OF_6AM)}
            />
            <span> 6:00 AM on the day of recycling</span>
          </label>
          <label className="pr-radio-item">
            <input
              type="radio"
              name="reminderTime"
              value={TIME_OPTIONS.DAY_BEFORE_6PM}
              checked={timeOption === TIME_OPTIONS.DAY_BEFORE_6PM}
              onChange={() => setTimeOption(TIME_OPTIONS.DAY_BEFORE_6PM)}
            />
            <span> 6:00 PM on the previous day</span>
          </label>
        </div>

        {modalSchedule && (
          <div className="pr-modal-hint">
            Event will be created for <b>{describeTimeOption(timeOption)}</b>.
          </div>
        )}
      </Modal>
    </div>
  );
}
